import { useState, useEffect } from 'react';
import { X, Play } from 'lucide-react';
import { cakisuno } from '../assets';

export default function YouTubePopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  useEffect(() => {
    if (!hasBeenShown) {
      const delay = Math.random() * 1000 + 4000;

      const timer = setTimeout(() => {
        setIsVisible(true);
        setHasBeenShown(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [hasBeenShown]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubscribe = () => {
    window.open(
      'https://www.youtube.com/@cakisuno?sub_confirmation=1',
      '_blank',
      'noopener,noreferrer'
    );
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backdropFilter: 'blur(6px)',
        backgroundColor: 'rgba(0,0,0,0.6)',
      }}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{
          maxWidth: '460px',
          borderRadius: '24px',
          background: '#0f0f0f',
          boxShadow:
            '0 0 0 1px rgba(255,255,255,0.08), 0 32px 80px rgba(0,0,0,0.8), 0 0 60px rgba(255,0,0,0.15)',
          animation:
            'popIn 0.45s cubic-bezier(0.34,1.56,0.64,1) forwards',
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          aria-label="Close popup"
          style={{
            position: 'absolute',
            top: '14px',
            right: '14px',
            zIndex: 10,
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#fff',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
          }}
        >
          <X size={16} />
        </button>

        {/* Hero Image Section */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '250px',
            overflow: 'hidden',
            background: '#0f0f0f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Red glow behind circle */}
          <div
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(200,0,0,0.25)',
              filter: 'blur(30px)',
            }}
          />

          <img
            src={cakisuno}
            alt="Ca Ki Suno"
            style={{
              width: '200px',
              height: '200px',
              objectFit: 'cover',
              objectPosition: 'center top',
              borderRadius: '50%',
              border: '3px solid rgba(220,0,0,0.7)',
              boxShadow:
                '0 0 0 4px rgba(220,0,0,0.15), 0 8px 32px rgba(0,0,0,0.6)',
              display: 'block',
              position: 'relative',
              zIndex: 1,
            }}
          />

          {/* Gradient overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(15,15,15,0.85) 90%, #0f0f0f 100%)',
            }}
          />
        </div>

        {/* Channel Name */}
        <div
          style={{
            textAlign: 'center',
            paddingTop: '14px',
          }}
        >
          <span
            style={{
              fontFamily: "'Georgia', serif",
              fontWeight: '700',
              fontSize: '22px',
              color: '#fff',
              letterSpacing: '0.04em',
              textShadow: '0 2px 12px rgba(0,0,0,0.8)',
              display: 'block',
            }}
          >
            Ca Ki Suno
          </span>

          <span
            style={{
              fontFamily: "'Georgia', serif",
              fontSize: '13px',
              color: 'rgba(255,80,80,0.9)',
              letterSpacing: '0.06em',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
            }}
          >
            <Play size={14} color="#ff4444" />
            @cakisuno
          </span>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '14px 24px 24px',
          }}
        >
          {/* Tagline */}
          <div
            style={{
              textAlign: 'center',
              marginBottom: '16px',
            }}
          >
            <h2
              style={{
                fontFamily: "'Georgia', serif",
                fontSize: '18px',
                fontWeight: '700',
                color: '#ffffff',
                margin: '0 0 6px',
                letterSpacing: '0.01em',
              }}
            >
              Join Our Community!
            </h2>

            <p
              style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.45)',
                margin: 0,
                fontFamily: "'Georgia', serif",
              }}
            >
              Subscribe and never miss a video
            </p>
          </div>

          {/* Divider */}
          <div
            style={{
              height: '1px',
              background:
                'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)',
              marginBottom: '16px',
            }}
          />

          {/* Benefits */}
          <div
            style={{
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '9px',
            }}
          >
            {[
              'New videos every day',
              'Behind-the-scenes content',
              'Join our YouTube channel',
            ].map((text, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background:
                      'linear-gradient(135deg, #ff4444, #cc0000)',
                    flexShrink: 0,
                    boxShadow: '0 0 6px rgba(255,60,60,0.6)',
                  }}
                />

                <span
                  style={{
                    fontSize: '13px',
                    color: 'rgba(255,255,255,0.7)',
                    fontFamily: "'Georgia', serif",
                  }}
                >
                  {text}
                </span>
              </div>
            ))}
          </div>

          {/* Subscribe Button */}
          <button
            onClick={handleSubscribe}
            style={{
              width: '100%',
              background:
                'linear-gradient(135deg, #ff0000, #cc0000)',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontFamily: "'Georgia', serif",
              fontWeight: '700',
              fontSize: '14px',
              letterSpacing: '0.05em',
              padding: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'transform 0.15s, box-shadow 0.15s',
              boxShadow: '0 4px 20px rgba(200,0,0,0.45)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow =
                '0 6px 28px rgba(200,0,0,0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow =
                '0 4px 20px rgba(200,0,0,0.45)';
            }}
          >
            <Play size={18} />
            SUBSCRIBE NOW
          </button>
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from {
            opacity: 0;
            transform: scale(0.88) translateY(16px);
          }

          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
}